import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { MessageType, useMessageReport } from "./MessageReporter"
import { useApi } from "../hooks/ApiProvider"
import { GetCurrentUser200Response } from "../api"
import { UserRole } from "../api/models/UserRole"
import { Permission } from "../api/models/Permission"

export const resolveUserPermissions = (
    user: GetCurrentUser200Response | undefined
): Permission[] => {
    const denied = new Set(user?.deniedPermissions ?? [])
    const granted = new Set([
        ...(user?.roleProperties?.permissions ?? []),
        ...(user?.allowedPermissions ?? []),
    ])
    return [...Array.from(granted)].filter((p) => !denied.has(p))
}

type RoleContextValue = {
    role?: UserRole
    permissions?: Permission[]
    me?: GetCurrentUser200Response
    reloadMe: () => Promise<void>
}

const RoleContext = React.createContext<RoleContextValue>({
    role: undefined,
    permissions: undefined,
    me: undefined,
    reloadMe: async () => undefined,
})

export const useRole = () => ({
    role: useContext(RoleContext).role,
    permissions: useContext(RoleContext).permissions,
    me: useContext(RoleContext).me,
    reloadMe: useContext(RoleContext).reloadMe,
})

export const RoleContextProvider = ({ children }: { children: React.ReactNode }) => {
    const { user } = useAuth0()
    const { addMessage } = useMessageReport() as {
        addMessage: (name: string, msg: string, type: string, err?: unknown) => void
    }
    const { consumerApi } = useApi()

    const [me, setJccUser] = useState<GetCurrentUser200Response | undefined>(undefined)
    const permissions = useMemo(() => resolveUserPermissions(me), [me])

    const reloadMe = useCallback(
        () =>
            consumerApi
                .getCurrentUser()
                .then(setJccUser)
                .catch((err) =>
                    addMessage("User role", "Could not get user role", MessageType.error, err)
                ),
        [consumerApi, addMessage]
    )

    useEffect(() => {
        if (user && !me) {
            reloadMe()
        }
    }, [reloadMe, user, me])

    return (
        <RoleContext.Provider value={{ role: me?.roleProperties, permissions, me, reloadMe }}>
            {children}
        </RoleContext.Provider>
    )
}
